/**
 * PDF rendering functions
 */

import jsPDF from 'jspdf';
import { ParsedLine } from '../leadsheetParser';
import { hexToRgb } from './pdfUtils';

export interface RenderOptions {
  fontSize: number;
  chordFontSize: number;
  chordColor: string;
  textOnly: boolean;
}

/**
 * Render a section header
 */
export function renderSectionHeader(
  doc: jsPDF,
  line: ParsedLine,
  x: number,
  y: number,
  fontSize: number
): number {
  doc.setFontSize(fontSize + 2);
  doc.setFont('courier', 'bold');
  doc.text(line.text, x, y);
  doc.setFont('courier', 'normal');
  
  return y;
}

/**
 * Render a line without chords
 */
export function renderTextLine(
  doc: jsPDF,
  line: ParsedLine,
  x: number,
  y: number,
  fontSize: number
): number {
  doc.setFontSize(fontSize);
  doc.text(line.text || ' ', x, y);
  return y + (fontSize * 0.45); // Line spacing based on text font size
}

/**
 * Render a line with chords
 */
export function renderLineWithChords(
  doc: jsPDF,
  line: ParsedLine,
  x: number,
  y: number,
  options: RenderOptions
): number {
  const { fontSize, chordFontSize, chordColor, textOnly } = options;

  if (textOnly) {
    // Text-only mode: render only text, no chords
    doc.setFontSize(fontSize);
    doc.setFont('courier', 'normal');
    const textToRender = line.text || ' ';
    doc.text(textToRender, x, y);
    return y + (fontSize * 0.45); // Normal line spacing
  }

  // Normal mode: render chords above text
  // Calculate vertical spacing based on chord font size
  const chordVerticalOffset = (chordFontSize * 0.28); // Reduced spacing between chords and text
  const chordYPosition = y - chordVerticalOffset;
  const textYPosition = y;

  // Set font for text to get accurate measurements
  doc.setFontSize(fontSize);
  doc.setFont('courier', 'normal');
  
  // Store chord positions and texts for rendering
  interface ChordToRender {
    text: string;
    x: number;
  }
  const chordsToRender: ChordToRender[] = [];
  
  // Calculate all chord positions first
  for (const chord of line.chords) {
    // Measure text up to chord position using actual space character width
    const spacesBeforeChord = ' '.repeat(chord.position);
    const xOffset = doc.getTextWidth(spacesBeforeChord);
    const idealX = x + xOffset;
    
    chordsToRender.push({
      text: chord.chord,
      x: idealX
    });
  }
  
  // Now render chords with collision detection
  doc.setFontSize(chordFontSize);
  doc.setFont('courier', 'bold');
  const [r, g, b] = hexToRgb(chordColor);
  doc.setTextColor(r, g, b);
  
  let lastChordEndX = -999;
  
  for (const chordInfo of chordsToRender) {
    const chordWidth = doc.getTextWidth(chordInfo.text);
    let actualX = chordInfo.x;
    
    // Prevent overlap - add minimum 2mm spacing
    if (actualX < lastChordEndX + 2) {
      actualX = lastChordEndX + 2;
    }
    
    doc.text(chordInfo.text, actualX, chordYPosition);
    lastChordEndX = actualX + chordWidth;
  }

  // Reset to black for lyrics
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(fontSize);
  doc.setFont('courier', 'normal');
  // Preserve leading/trailing spaces in text
  const textToRender = line.text || ' ';
  doc.text(textToRender, x, textYPosition);

  // Line spacing includes room for chords above (based on actual chord font size)
  return y + (fontSize * 0.45) + chordVerticalOffset;
}

/**
 * Calculate spacing after section header
 */
export function calculateSectionSpacing(
  nextLine: ParsedLine | null,
  fontSize: number,
  chordFontSize: number,
  textOnly: boolean
): number {
  if (!textOnly && nextLine && nextLine.chords.length > 0) {
    // Next line has chords - need more space for chord height
    return fontSize * 0.5 + (chordFontSize * 0.28);
  } else {
    // Next line has no chords - normal spacing
    return fontSize * 0.5;
  }
}

